import { Checkbox, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import type { Setting } from '../../../../context/settings-context';
import { useSettings } from '../../../../context/settings-context';
import type { I18nPrefix } from '../../../../i18n/prefix';
import { PARTNER_DOCS_URLS } from '../../constants';
import { buildOAuthAuthorizeUrl } from '../../oauth';
import { getOAuthClientIds } from '../../oauth-client-ids';
import type { PartnerModalProps } from '../../types';
import { authoriseAction, COMMON, PartnerModal, saveAction } from '../partner-modal';
import { buildOAuthRedirectUri, generateOAuthState, OAUTH, usePartnerModalSave } from './shared';

const KATANA = 'features:partners.components.katana' satisfies I18nPrefix;

function startOAuthFlow() {
  const state = generateOAuthState();
  localStorage.setItem(OAUTH.katana.stateKey, state);
  window.location.href = buildOAuthAuthorizeUrl('https://login.katanamrp.com/authorize', {
    response_type: 'code',
    client_id: getOAuthClientIds().katana,
    redirect_uri: buildOAuthRedirectUri(OAUTH.katana.redirectPath),
    scope: 'offline_access',
    audience: 'https://api.katanamrp.com',
    state,
  });
}

export function KatanaModal({ isOpen, onClose }: PartnerModalProps) {
  const settings = useSettings();
  const { save, isSaving } = usePartnerModalSave(onClose);
  // `integrationKatanaSendInvite` is `undefined` until the API has persisted it once.
  const [sendInvite, setSendInvite] = useState<boolean>(
    settings.integrationKatanaSendInvite ?? false,
  );

  const hasOAuthCode = !!settings.integrationKatanaOAuthCode;

  function handleSave() {
    const payload: Setting[] = [
      { key: 'integrationKatana', value: true },
      { key: 'integrationKatanaSendInvite', value: sendInvite },
    ];
    void save(payload);
  }

  return (
    <PartnerModal
      isOpen={isOpen}
      onClose={onClose}
      title="Katana"
      description={t(`${KATANA}.description`)}
      docsUrl={PARTNER_DOCS_URLS.katana}
      primaryAction={
        hasOAuthCode ? saveAction(handleSave, isSaving) : authoriseAction('Katana', startOAuthFlow)
      }
    >
      {!hasOAuthCode ? (
        <Text variant="secondary">{t(`${KATANA}.intro`)}</Text>
      ) : (
        <Checkbox
          name="sendInviteEmail"
          label={t(`${COMMON}.send_invite_label`, { partner: 'Katana' })}
          description={t(`${COMMON}.send_invite_description`)}
          checked={sendInvite}
          onChange={() => setSendInvite((prev) => !prev)}
        />
      )}
    </PartnerModal>
  );
}
